import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useService } from '@evertel/di'
import {
    Checkbox, RoomSearchSelect, Row, Col, Button, Text, Icon,
    Modal, ModalHeader, ModalBody, InfoBox, useUI,
    Tooltip, TooltipTrigger, TooltipContent
} from '@evertel/web/ui'
import { DocumentACLsController, DocumentSchemaStore } from '@evertel/document'
import { APIDataDocument, APIDataDocumentACL, APIDataRoom } from '@evertel/types'
import { DepartmentController } from '@evertel/department'
import { DepartmentAvatar } from '@evertel/web/department'
import { Links } from '@evertel/constants'
import { ACL } from './ACL'
import { DocumentIcon } from '../elements/DocumentIcon'
import { confirmUnsavedChanges } from '../../utils'

interface DocumentShareModalProps {
    documentACLController: DocumentACLsController
    document: APIDataDocument
    visible: boolean
    onClose: () => void
    backdrop?: boolean
}

/**
 * A room staged for sharing: an ACL without the server-assigned id, so the same <ACL>
 * row renders both staged and saved entries — mirrors mobile's RoomSelectionACL.
 */
interface RoomSelectionACL extends APIDataDocumentACL {
    isNew: true
}

type NotificationOption = 'notify' | 'urgent' | 'none'

// same order and copy as the mobile action sheet
const NOTIFICATION_OPTIONS: {
    value: NotificationOption
    label: string
    description: string
    urgent?: boolean
}[] = [
    {
        value: 'none',
        label: "Don't Post to Rooms",
        description: 'Share only: no room message is created.'
    },
    {
        value: 'notify',
        label: 'Post to Rooms',
        description: 'Creates a room message.'
    },
    {
        value: 'urgent',
        label: 'Post to Rooms as Urgent',
        description: 'Creates an urgent room message.',
        urgent: true
    }
]

export const DocumentShareModal: React.FC<DocumentShareModalProps> = observer(({
    documentACLController,
    document,
    visible = false,
    onClose,
    backdrop = true
}) => {
    const documentSchemaStore = useService(DocumentSchemaStore, [])

    const { addToast, isConfirmed } = useUI()

    const [isSaving, setIsSaving] = useState(false)
    // the ACL-shaped staging records, and the raw select options that back the chips
    const [newRooms, setNewRooms] = useState<RoomSelectionACL[]>([])
    const [selectedRooms, setSelectedRooms] = useState<APIDataRoom[]>([])
    const [roomsWithConnectors, setRoomsWithConnectors] = useState<Set<number>>(new Set())
    const [notificationOption, setNotificationOption] = useState<NotificationOption>('notify')
    const [originalACLs, setOriginalACLs] = useState<APIDataDocumentACL[]>([])
    const [currentACLs, setCurrentACLs] = useState<APIDataDocumentACL[]>([])

    // mirrors mobile's own guards, so the modal is safe wherever it's mounted and not
    // only where the caller happens to gate the button
    const blockedReason =
        (documentACLController.myAccessType !== undefined && !documentACLController.canShare)
            ? 'You do not have permission to share this document.'
            : (document?.isArchived)
                ? 'Archived documents can not be shared.'
                : null

    useEffect(() => {
        if (!visible || !blockedReason) return

        addToast({
            color: 'warning',
            title: 'Notice',
            message: blockedReason
        })
        onClose()
    }, [visible, blockedReason])

    const hasChanges = newRooms.length > 0 ||
        currentACLs.some(acl => {
            const original = originalACLs.find(orig => orig.id === acl.id)
            return original && (
                acl.accessType === null ||
                original.accessType !== acl.accessType
            )
        })

    // Seed the staging copies from the controller. Guarded on `hasChanges` so the ACL
    // refetches that addACL/removeACL trigger mid-save can never wipe out staged edits
    // the user still needs to see (e.g. after a partial failure).
    useEffect(() => {
        if (!visible || isSaving || hasChanges) return

        const acls = [...documentACLController.ACLs]

        setOriginalACLs(acls)
        setCurrentACLs(acls)
    }, [visible, isSaving, hasChanges, documentACLController.ACLs])

    const getAccessType = (access: string, currentAccessType: string) => {
        const accessType = DocumentACLsController.accessTypeForAction(access, currentAccessType)

        // belt and braces: the menu already hides what the user can't grant
        return documentACLController.canGrantAccessType(accessType) ? accessType : currentAccessType
    }

    const handleNewRoomACL = (access: string, acl: APIDataDocumentACL) => {
        if (access === 'delete') {
            setNewRooms(prev => prev.filter(room => room.principalId !== acl.principalId))
            setSelectedRooms(prev => prev.filter(room => room.id !== acl.principalId))
            return
        }

        setNewRooms(prev => prev.map(room =>
            room.principalId === acl.principalId
                ? { ...room, accessType: getAccessType(access, room.accessType) }
                : room
        ))
    }

    const handleExistingACL = (access: string, acl: APIDataDocumentACL) => {
        if (!acl.id) return

        const newAccessType = (access === 'delete') ? null : getAccessType(access, acl.accessType)

        setCurrentACLs(prev => prev.map(a =>
            a.id === acl.id ? { ...a, accessType: newAccessType } : a
        ))
    }

    const getItemState = (acl: APIDataDocumentACL) => {
        if (acl.accessType === null) return 'deleted'

        const original = originalACLs.find(orig => orig.id === acl.id)
        if (original && original.accessType !== acl.accessType) return 'modified'

        return 'normal'
    }

    const resetStagedChanges = () => {
        setNewRooms([])
        setSelectedRooms([])
        setCurrentACLs(originalACLs)
        setNotificationOption('notify')
    }

    const handleClose = async () => {
        // requests are already committing, so there's nothing safe to discard
        if (isSaving) return
        if (hasChanges && !(await confirmUnsavedChanges(isConfirmed))) return

        onClose()
    }

    const handleSave = async () => {
        if (!hasChanges || isSaving) return

        const notify = (notificationOption === 'none') ? undefined : {
            urgent: notificationOption === 'urgent'
        }

        try {
            setIsSaving(true)

            // existing ACLs first: addACL refetches the whole list, so it has to run last
            const updates = await Promise.allSettled(
                currentACLs.flatMap(acl => {
                    const original = originalACLs.find(orig => orig.id === acl.id)
                    if (!original) return []

                    if (acl.accessType === null) return [documentACLController.removeACL(acl.id)]
                    if (original.accessType !== acl.accessType) return [documentACLController.updateACL(acl.id, acl.accessType)]

                    return []
                })
            )

            const additions = await Promise.allSettled(
                newRooms.map(room =>
                    documentACLController.addACL(
                        room.principalId,
                        DocumentACLsController.principalTypes.Room,
                        room.accessType,
                        notify
                    )
                )
            )

            const failures = [...updates, ...additions].filter(result => result.status === 'rejected')

            if (failures.length) {
                // keep only the rooms that didn't make it, so the user can retry just those.
                // existing-ACL edits stay staged as-is, so a retry re-sends the ones that
                // already succeeded and they fail again — the toast is the signal to close
                // and start over.
                const failedRoomIds = new Set(
                    newRooms.filter((_, i) => additions[i].status === 'rejected').map(room => room.principalId)
                )
                setNewRooms(prev => prev.filter(room => failedRoomIds.has(room.principalId)))
                setSelectedRooms(prev => prev.filter(room => failedRoomIds.has(room.id)))

                addToast({
                    color: 'danger',
                    title: 'Oops!',
                    message: failures.map(failure => (failure as PromiseRejectedResult).reason?.message ?? 'Unknown error').join('\n')
                })
                return
            }

            // updateACL mutates ACLs in place, so the seeding effect above won't always re-run
            const updatedACLs = [...documentACLController.ACLs]
            setOriginalACLs(updatedACLs)
            setCurrentACLs(updatedACLs)

            setNewRooms([])
            setSelectedRooms([])
            setNotificationOption('notify')
            onClose()
        } finally {
            setIsSaving(false)
        }
    }

    const checkRoomConnectors = async (roomId: number) => {
        try {
            const count = await documentACLController.getRoomConnectorCount(roomId)
            if (count > 0) {
                setRoomsWithConnectors(prev => new Set(prev).add(roomId))
            }
        } catch {
            // silently ignore — worst case we don't show the warning
        }
    }

    const handleAddRoom = (rooms: APIDataRoom[]) => {
        const selectedIds = new Set(rooms.map(room => room.id))

        // keep the access already staged for rooms still selected, default the rest to READ
        const staged = rooms.map(room =>
            newRooms.find(existing => existing.principalId === room.id) ?? {
                isNew: true as const,
                accessType: DocumentACLsController.accessTypes.READ,
                principalType: DocumentACLsController.principalTypes.Room,
                principalId: room.id
            }
        )

        rooms
            .filter(room => !newRooms.some(existing => existing.principalId === room.id))
            .forEach(room => checkRoomConnectors(room.id))

        setNewRooms(staged)
        setSelectedRooms(rooms)

        // drop connector flags for rooms that are no longer selected
        setRoomsWithConnectors(prev => new Set([...prev].filter(id => selectedIds.has(id))))
    }

    if (!document?.id || blockedReason) return null

    const documentSchema = documentSchemaStore.findById(document.documentSchemaId)

    // same order as documentACLController.aclUsersAndRooms
    const userACLs = currentACLs.filter(acl => acl.principalType === DocumentACLsController.principalTypes.BlueUser)
    const roomACLs = currentACLs.filter(acl => acl.principalType === DocumentACLsController.principalTypes.Room)

    const connectorRoomStaged = newRooms.some(room => roomsWithConnectors.has(room.principalId))

    return (
        <Modal
            id="document-share-modal"
            visible={visible}
            alignment="center"
            backdrop={backdrop}
            onClose={handleClose}
            onCloseDone={resetStagedChanges}
            size="lg">
            <ModalHeader
                icon={documentSchema && <DocumentIcon schema={documentSchema} width={32} height={32} />}
                title={document.name ? `Share "${document.name}"` : 'Share EverDoc'}
            />
            <ModalBody className="pb-5">
                <Row>
                    <Col xs={10} valign="top">
                        <RoomSearchSelect
                            key={documentACLController.aclRooms.length}
                            searchScope="member"
                            placeholder="Add rooms..."
                            isDisabled={isSaving}
                            onSelect={handleAddRoom}
                            selected={selectedRooms}
                            excluded={documentACLController.aclRooms?.map(acl => acl.principalId)}
                            formatOptionLabel={(room: APIDataRoom, { context }: any) => {
                                if (context === 'value') {
                                    return room.name?.trim() || `RoomID:${room.id}`
                                }

                                return <RoomOptionLabel room={room} />
                            }}
                            className="mb-4"
                            tabSelectsValue={false}
                            isMulti
                        />
                    </Col>
                </Row>
                <div className="mb-4">
                    <Text bold size={13}>
                        Current Access
                    </Text>
                    <PerfectScrollbar
                        options={{ suppressScrollX: true }}
                        style={{ maxHeight: '30vh' }}>
                        {[...userACLs, ...roomACLs].map(acl => (
                            <ACL
                                attachId="document-share-modal"
                                key={acl.id}
                                acl={acl}
                                itemState={getItemState(acl)}
                                documentACLController={documentACLController}
                                onChangeACL={handleExistingACL}
                                busy={isSaving}
                            />
                        ))}
                    </PerfectScrollbar>
                </div>
                <Text bold size={13} className="mb-2">
                    Share to rooms
                </Text>
                {(!newRooms.length) && (
                    <Text tag="p" color="muted" size="smaller">
                        Rooms you add above will show up here.
                    </Text>
                )}
                {(!!newRooms.length) && (
                    <>
                        {connectorRoomStaged && (
                            <InfoBox color="warning" className="mb-3">
                                <Text>
                                    <strong>At least 1 room has external connectors enabled.</strong>
                                    <p>Your document may be sent to systems outside Evertel.  <a style={{color: 'currentColor', textDecoration: 'underline'}} href={Links.helpConnectors} target="_blank" rel="noopener noreferrer">
                                        Learn more
                                    </a></p>

                                </Text>
                            </InfoBox>
                        )}
                        <div className="document-share-tile">
                            {newRooms.map(room => (
                                <ACL
                                    attachId="document-share-modal"
                                    key={room.principalId}
                                    acl={room}
                                    isNew
                                    itemState="new"
                                    documentACLController={documentACLController}
                                    onChangeACL={handleNewRoomACL}
                                    busy={isSaving}
                                    trailingIcon={roomsWithConnectors.has(room.principalId) && (
                                        <Tooltip placement="top">
                                            <TooltipTrigger asChild>
                                                <span className="ms-2 text-info d-inline-flex align-items-center">
                                                    <Icon name="share-nodes" color="info-text-emphasis" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                This room has external connectors
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                />
                            ))}
                        </div>
                        <div
                            role="radiogroup"
                            aria-label="Room message options"
                            className="mt-3">
                            {NOTIFICATION_OPTIONS.map((option) => (
                                <div key={option.value} className="mb-1">
                                    <Checkbox
                                        radio
                                        urgent={option.urgent}
                                        id={`share-notify-${option.value}`}
                                        checked={notificationOption === option.value}
                                        disabled={isSaving}
                                        onChange={() => setNotificationOption(option.value)}
                                        label={option.label}
                                        inputProps={{ type: 'radio', name: 'share-notification' }}
                                    />
                                    <small className="d-block ms-4 ps-2 text-muted">
                                        {option.description}
                                    </small>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <Button
                    color="primary"
                    className="w-100 mt-3"
                    loading={isSaving}
                    disabled={!hasChanges}
                    onClick={handleSave}>
                    Save &amp; Share
                </Button>
            </ModalBody>
        </Modal>
    )
})

const RoomOptionLabel: React.FC<{ room: APIDataRoom }> = observer(({ room }) => {
    const departmentController = useService(DepartmentController, [room.departmentId])

    useEffect(() => {
        if (room.departmentId) {
            departmentController.init(room.departmentId)
        }
    }, [room.id])

    if (!room) return null

    const roomName = room.name?.trim() ? room.name : `RoomID:${room.id}`
    const department = departmentController?.department

    return (
        <div className="cursor-pointer">
            <Row>
                <Text bold>
                    # {roomName}
                </Text>
            </Row>
            {department && (
                <Row valign="center" className="mt-1">
                    <DepartmentAvatar
                        departmentId={department.id}
                        width={15}
                        height={15}
                        showGuestBadge={false}
                    />
                    <Text
                        color="muted"
                        size="small"
                        className="ms-1">
                        {departmentController.department.name}
                    </Text>
                </Row>
            )}
        </div>
    )
})
